/* kf-consent.js — opens the theme's own consent dialog (snippets/kf-consent.liquid)
 * and writes the visitor's choice through Shopify's Customer Privacy API. Spec 50.
 *
 * Flow: load the consent-tracking API → if shouldShowBanner() (unset consent in a
 * consent region) → showModal() once consent-banner.css has applied (it is loaded
 * async, spec 44 C). Accept / Odrzuć call setTrackingConsent for all four purposes
 * and close at once (as Shopify's own banner does; a write that fails leaves consent
 * unset, so the next page simply asks again — spec 50 D11). Preferencje hides this
 * dialog and opens Shopify's native preferences modal — a <dialog> in the top layer
 * would otherwise cover it; the page stays scroll-locked meanwhile, and if that modal
 * goes away without a decision (or never renders) this dialog reopens with its
 * previous focus restored. A decision is required (owner D4, 2026-09-14): Escape and
 * backdrop clicks are swallowed with a nudge animation and a live-region message; the
 * `close` fallback covers browsers whose close-watcher lets a second Escape through
 * regardless of preventDefault. The policy pages never render this snippet or script
 * (layout/theme.liquid, owner D8 a).
 *
 * Fallback (D12): Shopify's own banner is hidden only while `kf-consent-ready` is on
 * <html>. An inline script in layout/theme.liquid <head> sets that class before any of
 * Shopify's scripts run (so the banner never flashes, whatever the loading order) and
 * removes it again after 8 s unless this script has checked in (window.kfConsentReady) —
 * a script that never loads, or a browser without <dialog>, therefore ends with Shopify's
 * banner asking. This script takes the class back itself if the stylesheet or the consent
 * API never arrive, or if anything here throws. Fallen back = Shopify's banner, never both.
 */
(() => {
  const root = document.documentElement;
  const OPEN_CLASS = "kf-consent-open";
  const READY_CLASS = "kf-consent-ready";
  const dialog = document.getElementById("kf-consent");
  let fellBack = false;

  // give the decision back to Shopify's banner; once taken, nothing here opens again.
  // Shopify renders that banner on its own when due, but not always by the time we know
  // we failed (and not at all if its own decision ran earlier): ask for it explicitly.
  // Is a banner due? The same test Shopify's own banner makes (shouldShowGDPRBanner):
  // the region wants one AND the visitor has not chosen yet. shouldShowBanner() alone is
  // only the region flag — it stays true after a choice, which is what reopened the cloud
  // on every page on live (hotfix 2026-09-14). "No choice yet" is derived exactly as
  // Shopify's getTrackingConsent() does from currentVisitorConsent(): marketing and
  // analytics both "yes" = accepted, either "no" = declined, anything else (both empty,
  // or one "yes" and the other empty) = "no_interaction" → ask.
  const bannerDue = () => {
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp.shouldShowBanner !== "function" || !cp.shouldShowBanner()) return false;
    if (typeof cp.currentVisitorConsent !== "function") return true;
    const c = cp.currentVisitorConsent() || {};
    const accepted = c.marketing === "yes" && c.analytics === "yes";
    const declined = c.marketing === "no" || c.analytics === "no";
    return !accepted && !declined;
  };

  const fallBack = () => {
    fellBack = true;
    root.classList.remove(READY_CLASS);
    root.classList.remove(OPEN_CLASS);
    try {
      if (dialog && dialog.open) dialog.close();
      const pb = window.privacyBanner;
      if (bannerDue() && pb && typeof pb.showBanner === "function") pb.showBanner();
    } catch {
      /* Shopify's banner is its own concern from here */
    }
  };

  if (!dialog || typeof dialog.showModal !== "function" || window.kfConsentLate) {
    fallBack(); // no dialog to run, or the head's deadline already handed over to Shopify
    return;
  }
  window.kfConsentReady = true; // check in before the head's deadline
  root.classList.add(READY_CLASS); // idempotent: the head already set it

  // from here on anything thrown on the way in (a renamed Shopify API, a matchMedia or
  // showModal that refuses) must not leave Shopify's banner hidden with nothing in its
  // place: the whole set-up runs inside one try, and the async chain has its own catch
  try {
    const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)");
    const prefsButton = dialog.querySelector('[data-consent="prefs"]');
    let decided = false;
    let prefsOpen = false;
    let handoff = 0; // increments per hand-off so a stale watcher can never act
    let nudging = false;
    let lastFocus = null;
    let openedOnce = false;

    const privacy = () => (window.Shopify && window.Shopify.customerPrivacy) || null;

    // Idempotent: the dialog opens when the API says a banner is due and nothing has
    // been decided. Called once the API is ready — including when that readiness comes
    // AFTER the bounded wait below (a slow network must never leave a fresh visitor
    // without a prompt while Shopify's own banner is hidden by the ready class).
    const maybeOpen = () => {
      if (fellBack || decided || prefsOpen || dialog.open) return; // never over Shopify's banner or modal
      if (bannerDue()) open();
    };

    // resolves true when the API is there; false when nothing arrived within the wait
    const loadApi = () =>
      new Promise((resolve) => {
        if (privacy()) return resolve(true);
        if (!(window.Shopify && typeof window.Shopify.loadFeatures === "function")) return resolve(false);
        let settled = false;
        try {
          window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], () => {
            if (settled) {
              try {
                maybeOpen(); // late arrival (only if we did not fall back meanwhile): still run the eligibility check
              } catch {
                fallBack();
              }
              return;
            }
            settled = true;
            resolve(!!privacy());
          });
        } catch {
          settled = true;
          resolve(false);
        }
        setTimeout(() => {
          settled = true;
          resolve(!!privacy());
        }, 6000);
      });

    // resolves true only when the async sheet has actually applied (it is loaded with
    // media="print" and switched on load, spec 44 C); an error or a long wait is false —
    // a plain <dialog> without its stylesheet is not something to open over the page
    const cssReady = () =>
      new Promise((resolve) => {
        const link = document.querySelector('link[rel="stylesheet"][href*="consent-banner"]');
        if (!link) return resolve(false);
        if (link.media === "all") return resolve(true);
        link.addEventListener("load", () => resolve(link.media === "all"), { once: true }); // the inline onload must have switched it
        link.addEventListener("error", () => resolve(false), { once: true });
        setTimeout(() => resolve(link.media === "all"), 4000);
      });

    // remember where focus was inside the dialog, so a reopen puts it back (the
    // container itself resets it: a fresh open starts on the container, not a control)
    dialog.addEventListener("focusin", (event) => {
      lastFocus = event.target === dialog ? null : event.target;
    });

    const open = () => {
      if (dialog.open || decided || prefsOpen || fellBack) return;
      const restore = lastFocus; // read before showModal(): its own autofocus fires focusin too
      if (prefsButton) {
        // Preferencje only works while Shopify's privacyBanner is loaded (the Admin banner
        // setting); hide it rather than ship a silent no-op
        const pb = window.privacyBanner;
        prefsButton.hidden = !(pb && typeof pb.showPreferences === "function");
      }
      dialog.showModal();
      root.classList.add(OPEN_CLASS);
      if (restore && dialog.contains(restore)) restore.focus();
      else dialog.focus();
      if (!openedOnce) {
        // the entrance plays once: mark the dialog when it ends (or, with no animation under
        // reduced motion, after its duration) so a reopen never replays it
        openedOnce = true;
        const mark = () => dialog.setAttribute("data-opened", "");
        dialog.addEventListener(
          "animationend",
          (event) => {
            if (event.animationName === "kf-consent-in") mark();
          },
          { once: true }
        );
        setTimeout(mark, 400);
      }
    };

    // hide keeps the page locked (Shopify's modal is not in the top layer, so the
    // scroll lock is ours to hold); close releases it — only after a decision.
    const hide = () => {
      if (dialog.open) dialog.close();
    };

    const close = () => {
      root.classList.remove(OPEN_CLASS);
      hide();
    };

    const live = dialog.querySelector("[data-consent-live]");

    const nudge = () => {
      if (live) {
        // a decision is required: say so to screen readers (clear first so a repeat re-announces)
        live.textContent = "";
        setTimeout(() => {
          live.textContent = live.dataset.nudgeText || "";
        }, 50);
      }
      if (nudging) return;
      nudging = true;
      dialog.classList.add("is-nudged");
      let timer = 0;
      const done = () => {
        clearTimeout(timer);
        dialog.removeEventListener("animationend", onEnd);
        dialog.classList.remove("is-nudged");
        nudging = false;
      };
      const onEnd = (event) => {
        if (event.animationName === "kf-consent-nudge") done(); // the entrance animation ends here too
      };
      // the timer is the contract (a cancelled animation never fires animationend); under
      // reduce there is no animation at all: the class shows a static cue, then clears
      timer = setTimeout(done, reduceMotion && reduceMotion.matches ? 600 : 700);
      dialog.addEventListener("animationend", onEnd);
    };

    const setConsent = (granted) => {
      const cp = privacy();
      if (!cp || typeof cp.setTrackingConsent !== "function") return;
      decided = true;
      cp.setTrackingConsent(
        { analytics: granted, marketing: granted, preferences: granted, sale_of_data: granted },
        () => {}
      );
      close();
    };

    // Shopify's preferences modal has no privacy-policy link, a Polish close label or
    // descriptions tied to its checkboxes: add them once per modal instance (each a
    // no-op if the hook is ever renamed).
    const dressPrefs = (prefs) => {
      const intro = prefs.querySelector(".shopify-pc__prefs__intro-main");
      if (intro && !intro.querySelector(".kf-prefs-policy") && dialog.dataset.policyUrl) {
        const p = document.createElement("p");
        p.className = "kf-prefs-policy";
        const a = document.createElement("a");
        a.href = dialog.dataset.policyUrl;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.draggable = false;
        a.textContent = dialog.dataset.policyText || "";
        if (dialog.dataset.newTabText) {
          const cue = document.createElement("span");
          cue.className = "visually-hidden";
          cue.textContent = ` (${dialog.dataset.newTabText})`;
          a.appendChild(cue);
        }
        p.appendChild(a);
        intro.appendChild(p);
      }
      const closeBtn = prefs.querySelector("#shopify-pc__prefs__header-close");
      if (closeBtn && dialog.dataset.closeText) closeBtn.setAttribute("aria-label", dialog.dataset.closeText);
      prefs.querySelectorAll(".shopify-pc__prefs__option").forEach((option) => {
        const input = option.querySelector("input");
        const desc = option.querySelector("p");
        if (!input || !desc || input.getAttribute("aria-describedby")) return;
        if (!desc.id) desc.id = `${input.id}-desc`;
        input.setAttribute("aria-describedby", desc.id);
      });
    };

    const prefsVisible = (prefs) =>
      !!prefs &&
      prefs.isConnected &&
      prefs.getClientRects().length > 0 &&
      getComputedStyle(prefs).visibility !== "hidden";

    // the hand-off failed or ended without a decision: take it back
    const takeBack = (token) => {
      if (token !== handoff) return; // a newer hand-off owns the state now
      prefsOpen = false;
      if (!decided) open();
    };

    // Watch Shopify's modal until it goes away; reopen ours unless a decision landed
    // meanwhile (visitorConsentCollected below sets `decided`). No polling once it is
    // up: a MutationObserver on the node (style/class swaps) and on its parent (removal).
    // Until it is up: look every 150 ms for ~3 s, then treat it as never rendered.
    const watchPrefs = (token, attempt = 0) => {
      if (token !== handoff || decided) return;
      const prefs = document.getElementById("shopify-pc__prefs");
      if (!prefsVisible(prefs)) {
        if (attempt < 20) {
          setTimeout(() => watchPrefs(token, attempt + 1), 150);
          return;
        }
        takeBack(token);
        return;
      }
      dressPrefs(prefs);
      const observer = new MutationObserver(() => {
        if (prefsVisible(prefs)) return;
        observer.disconnect();
        takeBack(token);
      });
      observer.observe(prefs, { attributes: true, attributeFilter: ["style", "class", "hidden"] });
      if (prefs.parentNode) observer.observe(prefs.parentNode, { childList: true });
    };

    const showPrefs = () => {
      const pb = window.privacyBanner;
      if (!(pb && typeof pb.showPreferences === "function")) return;
      const token = ++handoff;
      prefsOpen = true;
      hide();
      // the watcher is the deadline: it starts whether or not showPreferences() settles
      setTimeout(() => watchPrefs(token), 150);
      let pending;
      try {
        pending = pb.showPreferences(); // a synchronous throw must not strand a hidden dialog
      } catch {
        takeBack(token);
        return;
      }
      Promise.resolve(pending).catch(() => takeBack(token));
    };

    dialog.addEventListener("click", (event) => {
      const button = event.target.closest("[data-consent]");
      if (button) {
        const action = button.dataset.consent;
        if (action === "accept") setConsent(true);
        else if (action === "decline") setConsent(false);
        else if (action === "prefs") showPrefs();
        return;
      }
      // anything outside the text block — the backdrop, the stage, the cloud's transparent
      // SVG corners — is a "not a choice" tap: nudge
      if (!event.target.closest(".kf-consent__content")) nudge();
    });

    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      nudge();
    });

    dialog.addEventListener("close", () => {
      // a close-watcher Escape got through: reopen (setTimeout also runs in a background
      // tab); re-check inside the callback — consent may have landed meanwhile
      if (!decided && !prefsOpen) {
        setTimeout(() => {
          if (!decided && !prefsOpen) open();
        }, 0);
      }
    });

    document.addEventListener("visitorConsentCollected", () => {
      decided = true;
      close();
    });

    Promise.all([loadApi(), cssReady()])
      .then(([api, css]) => {
        if (api && css) maybeOpen();
        else fallBack();
      })
      .catch(fallBack);
  } catch {
    fallBack();
  }
})();
